import React from 'react';
import { Box, Paper, Typography, Stack, useTheme, alpha } from '@mui/material';
import {
    SessionQuestionEntityFrontend,
    QuestionTypeFrontend,
    WorkerDtoFrontend,
    getAnswerForQuestion,
} from '../../../api/sessionQuestionsApi';
import { categoryConfig, getAnswerAsString, isAnswered } from './questionFormConstants';
import QuestionField from './QuestionField';

export interface QuestionFormSectionProps {
    category: QuestionTypeFrontend;
    questions: SessionQuestionEntityFrontend[];
    /** Worker entity for projecting current answer values (PR1 worker-as-truth). */
    worker: WorkerDtoFrontend | null;
    editedAnswers: Record<number, string>;
    dirtyQuestionIds: Set<number>;
    onFieldChange: (questionId: number, value: string) => void;
    onOpenHistory: (questionId: number) => void;
    onOpenListEditor: (questionId: number) => void;
}

const QuestionFormSection: React.FC<QuestionFormSectionProps> = ({
    category,
    questions,
    worker,
    editedAnswers,
    dirtyQuestionIds,
    onFieldChange,
    onOpenHistory,
    onOpenListEditor,
}) => {
    const theme = useTheme();
    const config = categoryConfig[category];
    if (!config) return null;

    const answeredCount = questions.filter(q => isAnswered(q)).length;
    const totalCount = questions.length;

    return (
        <Paper
            variant="outlined"
            sx={{
                borderRadius: 2,
                overflow: 'hidden',
                borderColor: alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.4 : 1),
            }}
        >
            {/* Section header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    backgroundColor: theme.palette.mode === 'dark'
                        ? alpha(config.color, 0.12)
                        : alpha(config.color, 0.06),
                    borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.3 : 0.6)}`,
                }}
            >
                <Box sx={{ color: config.color, display: 'flex', alignItems: 'center' }}>
                    {config.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {config.name}
                    </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                    {answeredCount}/{totalCount} answered
                </Typography>
            </Box>

            {/* Question fields */}
            <Stack divider={<Box sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}` }} />}>
                {questions.map((question) => {
                    const fieldValue = editedAnswers[question.id] !== undefined
                        ? editedAnswers[question.id]
                        : getAnswerAsString(question, getAnswerForQuestion(question, worker));

                    return (
                        <QuestionField
                            key={question.id}
                            question={question}
                            value={fieldValue}
                            isDirty={dirtyQuestionIds.has(question.id)}
                            onChange={(val) => onFieldChange(question.id, val)}
                            onOpenHistory={() => onOpenHistory(question.id)}
                            onOpenListEditor={() => onOpenListEditor(question.id)}
                        />
                    );
                })}
            </Stack>
        </Paper>
    );
};

export default QuestionFormSection;
